import { isLocalizedLocale } from './localized-routes';

/**
 * Site language — English by default, Arabic opt-in via a `/ar` path prefix.
 *
 * Deliberately separate from `LOCALIZED_LOCALES` in `localized-routes.ts`. Those
 * are English *regional* variants (en-bd / en-pk / en-eg) that exist only to keep
 * Webflow-localized URLs resolving and to emit reciprocal hreflang; they render
 * identical English copy. This module models a genuinely different language, with
 * its own content bundle and text direction.
 *
 * The two do meet in one place: a regional URL is still an English URL, so
 * `stripLanguagePrefix` drops an `en-*` segment as well. Without that,
 * `/en-eg/pricing` never resolves to `/pricing` and the Arabic switcher
 * disappears on every regional page even though the translation exists.
 */
export const LANGUAGES = ['en', 'ar'] as const;

export type Language = (typeof LANGUAGES)[number];

/**
 * English stays on the unprefixed canonical URLs.
 *
 * Typed as the literal `'en'` rather than `Language` so that comparing against it
 * narrows the union — that is what lets `hasTranslation` index the
 * translated-paths map, which has no English entry.
 */
export const DEFAULT_LANGUAGE = 'en' as const satisfies Language;

/** First path segment that selects each language ('' = no prefix). */
const LANGUAGE_SEGMENT: Record<Language, string> = { en: '', ar: 'ar' };

/** Endonyms — a language switcher should name each language in itself. */
export const LANGUAGE_LABELS: Record<Language, string> = { en: 'English', ar: 'العربية' };

/** `<html lang>` value. */
export const LANGUAGE_HTML_LANG: Record<Language, string> = { en: 'en', ar: 'ar' };

const RTL_LANGUAGES = new Set<Language>(['ar']);

const languageBySegment = new Map<string, Language>(
  LANGUAGES.filter((language) => LANGUAGE_SEGMENT[language]).map((language) => [
    LANGUAGE_SEGMENT[language],
    language,
  ]),
);

export function isLanguage(value: string): value is Language {
  return (LANGUAGES as readonly string[]).includes(value);
}

/**
 * Resolve the language from a pathname's first segment. Matching is on whole
 * segments, so `/article/...` is English rather than being mistaken for `ar`.
 */
export function languageFromPathname(pathname: string): Language {
  const [segment] = pathname.replace(/^\/+/u, '').split('/');
  return (segment && languageBySegment.get(segment.toLowerCase())) || DEFAULT_LANGUAGE;
}

/**
 * Split an href into its path and its `?query#hash` tail.
 *
 * The tail has to survive prefixing (`/pricing#fees` → `/ar/pricing#fees`) and
 * has to be off the string before any lookup, or an anchored link reads as a
 * different — and therefore untranslated — page.
 */
function splitPath(path: string): [pathname: string, suffix: string] {
  const index = path.search(/[?#]/u);
  return index === -1 ? [path, ''] : [path.slice(0, index), path.slice(index)];
}

/** Leading slash, no trailing slash (except at the root). */
function normalizePathname(pathname: string): string {
  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return withLeadingSlash.replace(/\/+$/u, '') || '/';
}

/**
 * Drop a leading language segment, returning the canonical (English) path.
 *
 * Regional English prefixes (`/en-eg`) are dropped too: they are English URLs,
 * so `/en-eg/pricing` and `/pricing` are the same page as far as translation
 * availability goes. A `?query#hash` tail is preserved and a trailing slash is
 * normalized away.
 */
export function stripLanguagePrefix(path: string): string {
  const [pathname, suffix] = splitPath(path);
  const [first = '', ...rest] = normalizePathname(pathname).replace(/^\/+/u, '').split('/');
  const isPrefix = languageBySegment.has(first.toLowerCase()) || isLocalizedLocale(first);
  const remainder = (isPrefix ? rest : [first, ...rest]).join('/');
  return `${remainder ? `/${remainder}` : '/'}${suffix}`;
}

/** Render a canonical path under a given language. */
export function pathForLanguage(path: string, language: Language): string {
  const [canonicalPath, suffix] = splitPath(stripLanguagePrefix(path));
  const segment = LANGUAGE_SEGMENT[language];
  if (!segment) return `${canonicalPath}${suffix}`;
  return canonicalPath === '/' ? `/${segment}${suffix}` : `/${segment}${canonicalPath}${suffix}`;
}

/**
 * Canonical paths that have a translated route, per language.
 *
 * This is the guard that keeps a partial rollout from producing dead links: a
 * translated page only exists once its route file exists, so linking to
 * `/ar/<path>` before then would 404. Add a path here in the same change that
 * adds its `ar.*.tsx` route — never before.
 *
 * English is the canonical language and always has every page, so it is not
 * listed.
 */
const TRANSLATED_PATHS: Record<Exclude<Language, 'en'>, ReadonlySet<string>> = {
  ar: new Set<string>(['/', '/pricing', '/business']),
};

/** Does `path` exist in `language`? */
export function hasTranslation(path: string, language: Language): boolean {
  if (language === DEFAULT_LANGUAGE) return true;
  const [pathname] = splitPath(stripLanguagePrefix(path));
  return TRANSLATED_PATHS[language].has(pathname);
}

/**
 * Path for `path` in `language`, or `undefined` when that language has no such
 * page. Callers decide whether to fall back to English or hide the link.
 */
export function translatedPathFor(path: string, language: Language): string | undefined {
  return hasTranslation(path, language) ? pathForLanguage(path, language) : undefined;
}

/** Languages that can render `path`, in declaration order. */
export function languagesWithTranslation(path: string): Language[] {
  return LANGUAGES.filter((language) => hasTranslation(path, language));
}

export function isRtl(language: Language): boolean {
  return RTL_LANGUAGES.has(language);
}

export function htmlDirForLanguage(language: Language): 'ltr' | 'rtl' {
  return isRtl(language) ? 'rtl' : 'ltr';
}
